import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { CartItem } from '@/pages/home/page';

interface NewProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  originalPrice: number;
  thumbnail: string;
  images: string[];
  description: string;
  sizes: string[];
  colors?: { name: string; hex: string; price?: number }[];
  stock: number;
  createdAt: string;
}

interface NewArrivalsProps {
  onAddToCart: (product: Omit<CartItem, 'quantity'>) => void;
}

const PAGE_SIZE = 8;

export default function NewArrivals({ onAddToCart }: NewArrivalsProps) {
  const [products, setProducts] = useState<NewProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0); // offset-based: page * PAGE_SIZE
  const isLoadingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Fetch a page of products
  const fetchPage = useCallback(async (offset: number, isFirst: boolean) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    if (isFirst) setLoading(true); else setLoadingMore(true);

    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, category, price, original_price, thumbnail, images, description, sizes, colors, stock, created_at')
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);

      if (!error && data) {
        const mapped: NewProduct[] = data.map((p: Record<string, unknown>) => ({
          id: p.id as string,
          name: p.name as string,
          category: p.category as string,
          price: p.price as number,
          originalPrice: (p.original_price as number) || (p.price as number),
          thumbnail: (p.thumbnail as string) || '',
          images: (p.images as string[]) || [],
          description: (p.description as string) || '',
          sizes: (p.sizes as string[]) || [],
          colors: p.colors as { name: string; hex: string; price?: number }[] | undefined,
          stock: (p.stock as number) || 0,
          createdAt: p.created_at as string,
        }));

        if (isFirst) {
          setProducts(mapped);
        } else {
          setProducts(prev => [...prev, ...mapped]);
        }
        setHasMore(data.length === PAGE_SIZE);
        setPage(offset + data.length);
      } else {
        setHasMore(false);
      }
    } catch (e) {
      console.error('NewArrivals fetch error:', e);
      setHasMore(false);
    } finally {
      if (isFirst) setLoading(false); else setLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchPage(0, true);

    // Real-time subscription — refresh first page on changes
    const sub = supabase
      .channel('new-arrivals')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, () => {
        fetchPage(0, true);
      })
      .subscribe();

    return () => { supabase.removeChannel(sub); };
  }, [fetchPage]);

  // IntersectionObserver — trigger load when sentinel is visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingRef.current) {
          fetchPage(page, false);
        }
      },
      { rootMargin: '250px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, page, fetchPage]);

  const handleProductClick = (product: NewProduct) => {
    const catMap: Record<string, string> = {
      'Track Pants': 'Track Pants',
      'T-Shirts': 'T-Shirts',
      'Jeans': 'Jeans',
      'Hoodies': 'Hoodies',
      'Shorts': 'Shorts',
    };
    const category = catMap[product.category] || product.category;
    navigate(`/category?cat=${encodeURIComponent(category)}&product=${product.id}`);
  };

  // Format relative time
  const getRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffHrs < 1) return 'Just now';
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  // onAddToCart kept for interface compatibility
  void onAddToCart;

  if (loading) {
    return (
      <section className="py-10 px-4 bg-gray-50/50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 rounded-xl bg-emerald-100 animate-pulse"></div>
            <div className="h-7 w-48 bg-gray-100 rounded-full animate-pulse"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden">
                <div className="h-48 bg-gray-100 animate-pulse"></div>
                <div className="p-3 space-y-2">
                  <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse"></div>
                  <div className="h-3 w-1/2 bg-gray-100 rounded animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!loading && products.length === 0) return null;

  return (
    <section className="py-10 px-4 bg-gradient-to-b from-white dark:from-slate-900 to-gray-50/50 dark:to-slate-800">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 mb-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-emerald-700 dark:text-emerald-400 text-xs font-bold uppercase tracking-wider">Fresh Drop</span>
            </div>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white leading-tight">
              New <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-600">Arrivals</span>
            </h2>
            <p className="text-gray-400 dark:text-gray-500 text-xs mt-0.5">
              {products.length} products loaded{hasMore ? ' · scroll for more' : ' · all caught up!'}
            </p>
          </div>
          <button
            onClick={() => navigate('/category?cat=Track Pants')}
            className="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-full bg-gray-900 dark:bg-slate-700 text-white text-xs font-bold hover:bg-gray-700 dark:hover:bg-slate-600 transition-all cursor-pointer whitespace-nowrap"
          >
            View All
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {products.map((product) => {
            const discount = product.originalPrice > product.price
              ? Math.round((1 - product.price / product.originalPrice) * 100) : 0;
            const image = product.thumbnail || product.images[0] || '';
            const relativeTime = getRelativeTime(product.createdAt);

            return (
              <div
                key={product.id}
                className="group na-card-3d cursor-pointer"
                onClick={() => handleProductClick(product)}
              >
                {/* Image */}
                <div className="relative w-full overflow-hidden" style={{ height: '180px', borderRadius: '16px 16px 0 0', background: 'linear-gradient(160deg, #f8fffe 0%, #f0fff4 100%)' }}>
                  {image ? (
                    <img
                      src={image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top na-img"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-t-shirt-line text-4xl text-gray-200"></i>
                    </div>
                  )}

                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none na-glass-overlay"></div>

                  {/* NEW Badge */}
                  <div className="absolute top-2.5 left-2.5">
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-black text-white tracking-wide"
                          style={{ background: 'linear-gradient(135deg, #10b981, #059669)', backdropFilter: 'blur(8px)', boxShadow: '0 2px 10px rgba(16,185,129,0.4)' }}>
                      ✦ NEW
                    </span>
                  </div>

                  {/* Discount badge */}
                  {discount > 0 && (
                    <div className="absolute top-2.5 right-2.5">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-black text-white"
                            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.15)' }}>
                        -{discount}%
                      </span>
                    </div>
                  )}

                  {/* Time badge */}
                  <div className="absolute bottom-2.5 left-2.5">
                    <span className="px-2.5 py-1 rounded-full text-[9px] font-bold flex items-center gap-1"
                          style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(10px)', color: '#374151', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
                      <i className="ri-time-line"></i>{relativeTime}
                    </span>
                  </div>
                </div>

                {/* Info panel */}
                <div className="p-3 na-info-panel" style={{ borderRadius: '0 0 16px 16px' }}>
                  <p className="text-[10px] uppercase tracking-widest font-black"
                     style={{ color: '#10b981' }}>{product.category}</p>
                  <h3 className="text-gray-900 font-black text-sm leading-tight mt-0.5 line-clamp-1">{product.name}</h3>
                  <p className="text-gray-400 text-[11px] mt-0.5 line-clamp-1">{product.description}</p>

                  {/* Price */}
                  <div className="flex items-center gap-1.5 mt-2 px-2 py-1.5 rounded-xl"
                       style={{ background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.12)' }}>
                    <span className="text-gray-900 font-black text-base">₹{product.price}</span>
                    {product.originalPrice > product.price && (
                      <span className="text-gray-300 line-through text-[11px]">₹{product.originalPrice}</span>
                    )}
                    {discount > 0 && (
                      <span className="ml-auto text-[10px] font-black px-1.5 py-0.5 rounded-full whitespace-nowrap"
                            style={{ background: 'rgba(16,185,129,0.12)', color: '#059669', border: '1px solid rgba(16,185,129,0.2)' }}>
                        {discount}% off
                      </span>
                    )}
                  </div>

                  {/* Sizes */}
                  {product.sizes.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {product.sizes.slice(0, 3).map(size => (
                        <span key={size} className="h-5 px-1.5 rounded-lg text-[10px] font-bold flex items-center justify-center"
                              style={{ background: 'rgba(0,0,0,0.05)', color: '#6b7280', border: '1px solid rgba(0,0,0,0.08)' }}>
                          {size}
                        </span>
                      ))}
                      {product.sizes.length > 3 && (
                        <span className="h-5 px-1.5 rounded-lg text-[10px] flex items-center"
                              style={{ color: '#9ca3af', background: 'rgba(0,0,0,0.04)' }}>
                          +{product.sizes.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* CTA */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleProductClick(product); }}
                    className="mt-2.5 w-full py-2.5 rounded-xl text-xs font-black transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 active:scale-95 na-btn-cart"
                  >
                    <i className="ri-eye-line"></i>View Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Infinite Scroll Sentinel */}
        <div ref={sentinelRef} className="w-full flex justify-center py-6">
          {loadingMore && (
            <div className="flex items-center gap-2 text-gray-400">
              <div className="w-5 h-5 rounded-full border-2 border-gray-200 border-t-emerald-500 animate-spin"></div>
              <span className="text-xs font-medium">Loading more arrivals...</span>
            </div>
          )}
        </div>

        {/* End of list */}
        {!hasMore && products.length > 0 && (
          <div className="flex flex-col items-center gap-3 pb-4">
            <div className="w-12 h-1 rounded-full bg-gray-200 dark:bg-slate-600"></div>
            <p className="text-gray-400 text-xs font-medium">Sab dekh liya! {products.length} new arrivals</p>
            <button
              onClick={() => navigate('/category?cat=Track Pants')}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-gray-900 dark:bg-slate-700 text-white text-xs font-bold hover:bg-gray-700 transition-all cursor-pointer whitespace-nowrap"
            >
              Browse All Categories <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        )}

        {/* Mobile View All button (only when more products exist) */}
        {hasMore && (
          <div className="sm:hidden mt-2">
            <button
              onClick={() => navigate('/category?cat=Track Pants')}
              className="w-full py-3 rounded-xl bg-gray-900 dark:bg-slate-700 text-white text-sm font-bold flex items-center justify-center gap-2 cursor-pointer"
            >
              View All Products
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        )}
      </div>

      <style>{`
        .na-card-3d {
          border-radius: 18px;
          background: linear-gradient(145deg, #ffffff 0%, #f8fff9 100%);
          border: 1px solid rgba(16,185,129,0.1);
          box-shadow: 0 2px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04);
          transition: transform 0.35s cubic-bezier(0.23,1,0.32,1), box-shadow 0.35s ease;
          overflow: hidden;
          position: relative;
        }
        .na-card-3d:hover {
          transform: translateY(-6px) scale(1.015) rotateX(2deg);
          box-shadow: 0 16px 40px rgba(16,185,129,0.15), 0 6px 12px rgba(0,0,0,0.08);
        }
        .na-card-3d::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.6) 50%, transparent 70%);
          background-size: 200% 100%;
          pointer-events: none;
          z-index: 5;
          opacity: 0;
          transition: opacity 0.3s;
          border-radius: inherit;
        }
        .na-card-3d:hover::before {
          opacity: 1;
          animation: naShine 0.6s ease forwards;
        }
        @keyframes naShine {
          from { background-position: -200% center; }
          to   { background-position: 200% center; }
        }
        .na-glass-overlay {
          background: linear-gradient(to top, rgba(0,0,0,0.15) 0%, transparent 50%);
        }
        .na-img {
          transition: transform 0.6s cubic-bezier(0.23,1,0.32,1);
        }
        .na-card-3d:hover .na-img {
          transform: scale(1.08);
        }
        .na-info-panel {
          background: linear-gradient(to bottom, rgba(255,255,255,0.98), rgba(248,255,252,0.99));
          border-top: 1px solid rgba(16,185,129,0.1);
        }
        .na-btn-cart {
          background: linear-gradient(135deg, #1a1a2e, #16213e);
          color: white;
          box-shadow: 0 3px 12px rgba(0,0,0,0.2);
        }
        .na-btn-cart:hover {
          transform: scale(1.02);
          box-shadow: 0 6px 18px rgba(0,0,0,0.25);
        }
      `}</style>
    </section>
  );
}
